import React, { useEffect, useState, useCallback } from 'react';
import withSession from '../lib/withSession';
import { Query } from 'react-apollo';
import { APPOINTMENTS } from '../graphql';
import PleaseWait from '../components/PleaseWait';
import styled from 'styled-components';
import { Box, Button, Flex, Heading, Text } from 'rebass';
import { isMobile } from 'react-device-detect';
import { ButtonWithSelectionCircle } from '../components/intake/components/ButtonWithSelectionCircle';
import { withRouter } from 'react-router';
import { isAsyncAppointment, isMenoAppointment, isWeightLossAppointment } from '../utils/appointments';
import moment from 'moment';
import momentTz from 'moment-timezone';

const Container = styled(Box)`
  max-width: 600px;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  z-index: 1;
  overflow-y: ${(props) => (isMobile ? 'hidden' : '')};
`;

const InnerContainerInformation = styled(Flex)`
  max-width: ${(props) => (isMobile ? 'calc(100% - 12px)' : '850px')};
  height: auto;
  margin: 0 auto;
  max-height: 100%;
  flex-direction: column;
  overflow-y: ${(props) => (isMobile ? 'auto' : '')};
`;

const getUpcomingAppointments = (appointments, filterFn) => {
  return appointments.filter(filterFn).sort((a, b) => {
    const ma = moment(`${a.date} ${a.startTime}`, 'MM/DD/YYYY HH:mm');
    const mb = moment(`${b.date} ${b.startTime}`, 'MM/DD/YYYY HH:mm');
    return ma.isBefore(mb) ? -1 : 1;
  });
};

const getPastAppointment = (appointments, filterFn) => {
  return appointments
    .sort((a, b) => {
      const ma = moment(`${a.date} ${a.startTime}`, 'MM/DD/YYYY HH:mm');
      const mb = moment(`${b.date} ${b.startTime}`, 'MM/DD/YYYY HH:mm');
      return ma.isAfter(mb) ? -1 : 1;
    })
    .find(filterFn);
};

const SmartIntakeInner = ({
  dataAppointments,
  loadingAppointments,
  history,
  session: { intakeStatus, menoIntakeStatus, weightLossIntakeStatus, weightLossIntakeDate, intakeDate, menoIntakeDate },
}) => {
  const [appointmentsState, setAppointmentsState] = useState({
    hasFutureAppointments: false,
    upcomingAppointment: null,
    upcomingAppointmentMeno: null,
    upcomingAppointmentWeightLoss: null,
    pastAppointment: null,
    pastAppointmentMeno: null,
    pastAppointmentWeightLoss: null,
    mustCompleteIntake: false,
    mustCompleteIntakeMeno: false,
    mustCompleteIntakeWeightloss: false,
  });

  const { upcomingAppointment, upcomingAppointmentMeno, upcomingAppointmentWeightLoss, mustCompleteIntake, mustCompleteIntakeMeno, mustCompleteIntakeWeightloss } = appointmentsState;

  const processAppointments = useCallback(
    () => {
      if (!dataAppointments || !dataAppointments.appointments) return;

      const appointments = dataAppointments.appointments;

      // Get future appointments
      const upcoming = getUpcomingAppointments(
        appointments,
        (item) => item.status === 'f' && !isWeightLossAppointment(item.appointmentTypeId) && !isAsyncAppointment(item.appointmentTypeId) && !isMenoAppointment(item.appointmentTypeId)
      );

      const upcomingMeno = getUpcomingAppointments(appointments, (item) => item.status === 'f' && isMenoAppointment(item.appointmentTypeId));

      const upcomingWeightloss = getUpcomingAppointments(appointments, (item) => item.status === 'f' && isWeightLossAppointment(item.appointmentTypeId));

      // Set the first upcoming appointment for each type
      setAppointmentsState((prevState) => ({
        ...prevState,
        upcomingAppointment: upcoming[0] || null,
        upcomingAppointmentMeno: upcomingMeno[0] || null,
        upcomingAppointmentWeightLoss: upcomingWeightloss[0] || null,
      }));

      // Check for future appointments in general
      const hasFuture = upcoming.find((item) => {
        const dt = momentTz(`${item.date} ${item.startTime}`, 'MM/DD/YYYY HH:mm').tz(item.timezoneName);
        return dt.isAfter(
          momentTz()
            .tz(item.timezoneName)
            .add(-120, 'minutes'),
          'minute'
        );
      });

      const hasFutureMeno = upcomingMeno.find((item) => {
        const dt = momentTz(`${item.date} ${item.startTime}`, 'MM/DD/YYYY HH:mm').tz(item.timezoneName);
        return dt.isAfter(
          momentTz()
            .tz(item.timezoneName)
            .add(-120, 'minutes'),
          'minute'
        );
      });

      const hasFutureWeightloss = upcomingWeightloss.find((item) => {
        const dt = momentTz(`${item.date} ${item.startTime}`, 'MM/DD/YYYY HH:mm').tz(item.timezoneName);
        return dt.isAfter(
          momentTz()
            .tz(item.timezoneName)
            .add(-120, 'minutes'),
          'minute'
        );
      });

      setAppointmentsState((prevState) => ({
        ...prevState,
        hasFutureAppointments: hasFuture || false,
        hasFutureAppointmentsMeno: hasFutureMeno || false,
        hasFutureAppointmentsWeightloss: hasFutureWeightloss || false,
      }));

      // Get past appointments
      const past = getPastAppointment(
        appointments,
        (item) =>
          item.status !== 'x' &&
          item.status !== 'f' &&
          item.status !== 'o' &&
          !isAsyncAppointment(item.appointmentTypeId) &&
          !isMenoAppointment(item.appointmentTypeId) &&
          !isWeightLossAppointment(item.appointmentTypeId) &&
          moment(`${item.date} ${item.startTime}`, 'MM/DD/YYYY HH:mm').isBefore(momentTz().tz(item.timezoneName), 'minute')
      );

      const pastMeno = getPastAppointment(
        appointments,
        (item) =>
          item.status !== 'x' &&
          item.status !== 'f' &&
          item.status !== 'o' &&
          isMenoAppointment(item.appointmentTypeId) &&
          moment(`${item.date} ${item.startTime}`, 'MM/DD/YYYY HH:mm').isBefore(momentTz().tz(item.timezoneName), 'minute')
      );

      const pastWeightLoss = getPastAppointment(
        appointments,
        (item) =>
          item.status !== 'x' &&
          item.status !== 'f' &&
          item.status !== 'o' &&
          isWeightLossAppointment(item.appointmentTypeId) &&
          moment(`${item.date} ${item.startTime}`, 'MM/DD/YYYY HH:mm').isBefore(momentTz().tz(item.timezoneName), 'minute')
      );

      setAppointmentsState((prevState) => ({
        ...prevState,
        pastAppointment: past || null,
        pastAppointmentMeno: pastMeno || null,
        pastAppointmentWeightLoss: pastWeightLoss || null,
      }));
    },
    [dataAppointments]
  );

  useEffect(
    () => {
      if (!loadingAppointments) {
        processAppointments();
      }
    },
    [dataAppointments, loadingAppointments, processAppointments]
  );

  const checkIntakeCompletion = useCallback(
    () => {
      if (loadingAppointments) return;

      const { hasFutureAppointments, hasFutureAppointmentsMeno, hasFutureAppointmentsWeightloss, pastAppointment, pastAppointmentMeno, pastAppointmentWeightLoss } = appointmentsState; // Destructure from appointmentsState

      const checkIntake = (hasFuture, status, date, pastAppointment) =>
        hasFuture && (status !== 'completed' || !date || (pastAppointment && pastAppointment.date && momentTz(date, 'MM-DD-YYYY').isSameOrBefore(momentTz(pastAppointment.date, 'MM/DD/YYYY'), 'day')));

      setAppointmentsState((prevState) => ({
        ...prevState,
        mustCompleteIntake: checkIntake(hasFutureAppointments, intakeStatus, intakeDate, pastAppointment),
        mustCompleteIntakeMeno: checkIntake(hasFutureAppointmentsMeno, menoIntakeStatus, menoIntakeDate, pastAppointmentMeno),
        mustCompleteIntakeWeightloss: checkIntake(hasFutureAppointmentsWeightloss, weightLossIntakeStatus, weightLossIntakeDate, pastAppointmentWeightLoss),
      }));
    },
    [appointmentsState, intakeStatus, intakeDate, menoIntakeStatus, menoIntakeDate, weightLossIntakeStatus, weightLossIntakeDate, loadingAppointments]
  );

  useEffect(
    () => {
      checkIntakeCompletion();
    },
    [checkIntakeCompletion]
  );

  if (loadingAppointments) {
    return (
      <Flex flex={1} alignItems={'center'} justifyContent={'center'}>
        <PleaseWait />
      </Flex>
    );
  }

  return (
    <Container>
      <InnerContainerInformation p={3} height={'auto'}>
        <Flex p={4} mb={4}>
          <Flex mb={4} flexDirection="column">
            <Flex flex={1} justifyContent="center" alignItems="center" flexDirection="column">
              <Heading mt={4} mb={4} textAlign={'center'}>
                Intake Forms to Complete for your Visits:
              </Heading>
              {!mustCompleteIntake &&
                !mustCompleteIntakeMeno &&
                !mustCompleteIntakeWeightloss && (
                  <Flex flex={1} flexDirection={'column'} justifyContent={'center'} alignItems={'center'}>
                    <Text textAlign="center" mt="4px" style={{ fontWeight: 700 }}>
                      You're all set!
                    </Text>
                    <Text textAlign="center" mt={3} style={{ fontWeight: 300 }}>
                      You’ve already completed all required intake questionnaires. <br />
                      If anything changes or you’d like to update your information, please email us <a href="mailto:contact@palomahealth.com">contact@palomahealth.com</a>.
                    </Text>
                    <Button
                      variant={'primary'}
                      mt={4}
                      onClick={() => {
                        history.push('/');
                      }}
                    >
                      Go to dashboard
                    </Button>
                  </Flex>
                )}

              {!!mustCompleteIntake && (
                <ButtonWithSelectionCircle type="button" style={{ width: '100%' }} variant={'white'} mb={4} onClick={() => history.push('/intake')}>
                  <Flex flex={1} alignItems={'center'}>
                    <Flex flex={1} flexDirection={'column'}>
                      <Text textAlign="left" mt="4px" style={{ fontWeight: 700 }}>
                        Hypothyroidism Intake Form
                      </Text>
                      {!!upcomingAppointment && (
                        <Text textAlign="left" mt={2} style={{ fontWeight: 300, opacity: 0.75, fontSize: '13px' }}>
                          {`Visit scheduled for ${upcomingAppointment.date} ${moment(upcomingAppointment.startTime, 'HH:mm').format('hh:mm A')}`}
                        </Text>
                      )}
                    </Flex>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#344f79"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ opacity: 0.65 }}
                    >
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </Flex>
                </ButtonWithSelectionCircle>
              )}

              {!!mustCompleteIntakeMeno && (
                <ButtonWithSelectionCircle type="button" style={{ width: '100%' }} variant={'white'} mb={4} onClick={() => history.push('/intake-menopause')}>
                  <Flex flex={1} alignItems={'center'}>
                    <Flex flex={1} flexDirection={'column'}>
                      <Text textAlign="left" mt="4px" style={{ fontWeight: 700 }}>
                        Menopause Intake Form
                      </Text>
                      {!!upcomingAppointmentMeno && (
                        <Text textAlign="left" mt={2} style={{ fontWeight: 300, opacity: 0.75, fontSize: '13px' }}>
                          {`Visit scheduled for ${upcomingAppointmentMeno.date} ${moment(upcomingAppointmentMeno.startTime, 'HH:mm').format('hh:mm A')}`}
                        </Text>
                      )}
                    </Flex>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#344f79"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ opacity: 0.65 }}
                    >
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </Flex>
                </ButtonWithSelectionCircle>
              )}

              {!!mustCompleteIntakeWeightloss && (
                <ButtonWithSelectionCircle type="button" style={{ width: '100%' }} variant={'white'} mb={4} onClick={() => history.push('/intake-weightloss')}>
                  <Flex flex={1} alignItems={'center'}>
                    <Flex flex={1} flexDirection={'column'}>
                      <Text textAlign="left" mt="4px" style={{ fontWeight: 700 }}>
                        Weight Loss Intake Form
                      </Text>
                      {!!upcomingAppointmentWeightLoss && (
                        <Text textAlign="left" mt={2} style={{ fontWeight: 300, opacity: 0.75, fontSize: '13px' }}>
                          {`Visit scheduled for ${upcomingAppointmentWeightLoss.date} ${moment(upcomingAppointmentWeightLoss.startTime, 'HH:mm').format('hh:mm A')}`}
                        </Text>
                      )}
                    </Flex>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#344f79"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ opacity: 0.65 }}
                    >
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </Flex>
                </ButtonWithSelectionCircle>
              )}
            </Flex>
          </Flex>
        </Flex>
      </InnerContainerInformation>
    </Container>
  );
};

const SmartIntake = ({ session, history }) => (
  <Query query={APPOINTMENTS}>
    {({ data: dataAppointments, loading: loadingAppointments }) => (
      <SmartIntakeInner session={session} dataAppointments={dataAppointments} loadingAppointments={loadingAppointments} history={history} />
    )}
  </Query>
);

export default withSession(withRouter(SmartIntake));
